import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { HttpRequestModel } from '@models/http-request.model';
import { HttpRequestActions } from '@states/http-request/http-request.action-types';
import { unblockHttpRequestSuccess } from '@states/http-request/http-request.actions';

export interface HttpRequestState extends EntityState<HttpRequestModel.DocumentMongo> {
  isInitialLoaded: boolean; // show loader until first loading happening
  isCreating: boolean;
  deletingHttpRequestIds: string[];
  notEmpty: boolean; //show no Data if result empty
  isKeyDeleting: boolean;
  isKeyLoading: boolean;
}

export const adapter: EntityAdapter<HttpRequestModel.DocumentMongo> = createEntityAdapter<HttpRequestModel.DocumentMongo>({
  selectId: (document: HttpRequestModel.DocumentMongo) => document._id,
});

const { selectAll } = adapter.getSelectors();


const initialState: HttpRequestState = adapter.getInitialState({
  isInitialLoaded: false,
  isCreating: false,
  deletingHttpRequestIds: [],
  notEmpty: null,
  isKeyDeleting: false,
  isKeyLoading: false,
});


export const httpRequestStateReducer = createReducer(
  initialState,
  on(HttpRequestActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(HttpRequestActions.getOrganizationHttpRequestsSuccess, (state, { documents }) => {
    return adapter.setAll(documents, {
      ...state,
      isInitialLoaded: true,
      notEmpty: !state.isInitialLoaded ? !!documents.length : state.notEmpty,
    });
  }),
  on(HttpRequestActions.getOrganizationHttpRequestsFail, (state) => {
    return {
      ...state,
      isInitialLoaded: true,
    };
  }),
  on(HttpRequestActions.setIsCreatingLoader, (state, { isCreating }) => {
    return {
      ...state,
      isCreating,
    };
  }),
  on(HttpRequestActions.setIsKeyDeleting, (state, { isKeyDeleting }) => {
    return {
      ...state,
      isKeyDeleting,
    };
  }),
  on(HttpRequestActions.setIsKeyLoading, (state, { isKeyLoading }) => {
    return {
      ...state,
      isKeyLoading,
    };
  }),
  on(HttpRequestActions.startDeleteHttpRequest, (state, { id }) => {
    return {
      ...state,
      deletingHttpRequestIds: [...state.deletingHttpRequestIds, id],
    };
  }),
  on(HttpRequestActions.sendDeleteHttpRequestRequestSuccess, (state, { id }) => {
    const deletingIndex = state.deletingHttpRequestIds.findIndex(_id => _id === id);
    let newDeletingArray = [...state.deletingHttpRequestIds];
    newDeletingArray.splice(deletingIndex, 1);
    return adapter.removeOne(id, {
      ...state,
      deletingHttpRequestIds: newDeletingArray,
      notEmpty: selectAll(state).length > 1,
    });
  }),
  on(HttpRequestActions.sendDeleteHttpRequestRequestFail, (state, { id }) => {
    const deletingIndex = state.deletingHttpRequestIds.findIndex(_id => _id === id);
    let newDeletingArray = [...state.deletingHttpRequestIds];
    newDeletingArray.splice(deletingIndex, 1);
    return {
      ...state,
      deletingHttpRequestIds: newDeletingArray,
    };
  }),
  on(HttpRequestActions.sendCreateHttpRequestRequestSuccess, (state, { httpRequest }) => {
    return adapter.setAll([httpRequest, ...selectAll(state)], { ...state, notEmpty: true });
  }),
  on(HttpRequestActions.sendUpdateHttpRequestRequestSuccess, (state, { httpRequest }) => {
    return adapter.updateOne({
      id: httpRequest._id,
      changes: {
        ...httpRequest,
      },
    }, { ...state });
  }),
  on(HttpRequestActions.getBlockedHttpRequestsSuccess, (state, { blockedScopeIds }) => {
    return adapter.map((entity) => {
      const isBlocked = blockedScopeIds.includes(entity._id);
      return isBlocked ? { ...entity, isBlocked: true } : entity;
    }, {
      ...state,
    });
  }),
  on(HttpRequestActions.unblockHttpRequestSuccess, (state, { id }) => {
    return adapter.updateOne({
      id: id,
      changes: {
        isBlocked: false,
      },
    }, { ...state });
  }),
);
